import React, { useEffect, useState } from "react";
import MappingTopBox from "./CreatorTools/MappingTool/components/MappingTopBox";
import { Tpa as TPA, Carrier } from "@rebuild/shared/models";
import { RuleTemplateInputField } from "@rebuild/shared";
import { InputFieldComponent } from "./components/InputFields/InputFieldComponent";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { useFirestoreCollectionReadWrite } from "../hooks/useFirestoreCollectionReadWrite";

const TPA_INPUT_FIELD: RuleTemplateInputField = {
	name: "tpa",
	type: "object",
	properties: [
		{
			name: "name",
			type: "string",
		},
		{
			name: "disabled",
			type: "boolean",
		},
		{
			name: "isDev",
			type: "boolean",
		},
	],
};

const CARRIER_INPUT_FIELD: RuleTemplateInputField = {
	name: "carrier",
	type: "object",
	properties: [
		{
			name: "name",
			type: "string",
		},
		{
			name: "disabled",
			type: "boolean",
		},
		{
			name: "isDev",
			type: "boolean",
		},
	],
};

interface InputFieldModalProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	inputField: RuleTemplateInputField;
	inputValue: any;
	onUpdate: (updates: any) => void;
	onSave: () => void;
	saveButtonLabel: string;
}

const InputFieldModal: React.FC<InputFieldModalProps> = ({ isOpen, onClose, title, inputField, inputValue, onUpdate, onSave, saveButtonLabel }) => {
	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
			<div className="bg-white rounded-lg shadow-lg w-96 relative">
				<button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl">
					×
				</button>
				<div className="p-6">
					<h2 className="text-xl font-bold text-gray-800 mb-6">{title}</h2>
					<div className="space-y-4">
						<InputFieldComponent
							objectInput={inputValue}
							inputField={inputField}
							inputValue={inputValue}
							path={inputField.name}
							onUpdate={onUpdate}
						/>
					</div>
					<div className="mt-6">
						<button onClick={onSave} className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors">
							{saveButtonLabel}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

interface BaseObject {
	id: string;
	disabled?: boolean;
	isDev?: boolean;
}

interface ObjectListProps<T extends BaseObject> {
	firestorePath: string;
	inputField: RuleTemplateInputField;
	title: string;
	entityName: string;
	emptyMessage: string;
	getDisplayName: (obj: T) => string;
}

/**
 * Generic component for loading a list of objects from a firestore collection in a column, updating existing objects, and creating new objects.
 */
const ObjectList = <T extends BaseObject>({ firestorePath, inputField, title, entityName, emptyMessage, getDisplayName }: ObjectListProps<T>) => {
	const { objects, isLoading, saveObject } = useFirestoreCollectionReadWrite<T>(firestorePath);

	const [currentObject, setCurrentObject] = useState<Partial<Omit<T, "id">> | T | undefined>();

	const onSave = async () => {
		try {
			if (currentObject === undefined) {
				throw new Error(`Attempting to save an object that is undefined, this shouldn't happen`);
			}

			const obj = "id" in currentObject ? currentObject : { id: uuidv4(), ...currentObject };

			await saveObject((obj as T).id, obj as T);
		} catch (e) {
			toast.error(`Encountered error when saving ${entityName.toLowerCase()}`);
		}
		setCurrentObject(undefined);
	};

	const openModalForCreate = () => {
		setCurrentObject({});
	};

	const openModalForEdit = (obj: T) => {
		setCurrentObject(obj);
	};

	const onObjectUpdate = (updates: Record<string, Partial<Omit<T, "id">> | T>) => {
		setCurrentObject(updates[inputField.name]);
	};

	if (!objects?.length && isLoading) {
		return <>Loading...</>;
	}

	const isModalOpen = currentObject !== undefined;
	const isEditing = isModalOpen && "id" in currentObject;

	return (
		<>
			<div className="bg-white p-6 rounded-lg shadow">
				<div className="flex justify-between items-center mb-6">
					<h2 className="text-xl font-semibold text-gray-800">{title}</h2>
					<button onClick={openModalForCreate} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
						Add {entityName}
					</button>
				</div>

				<div className="space-y-3">
					{objects.map((obj) => (
						<div key={obj.id} className="border p-4 rounded-lg flex justify-between items-center hover:bg-gray-50">
							<div className="flex items-center gap-4">
								<span className="font-medium text-gray-800">{getDisplayName(obj)}</span>
								<span className={`text-xs px-2 py-1 rounded-full ${obj.disabled ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>
									{obj.disabled ? "Disabled" : "Enabled"}
								</span>
								{obj.isDev && <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">Dev</span>}
							</div>
							<button
								onClick={() => openModalForEdit(obj)}
								className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
								title="Edit"
							>
								<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
									/>
								</svg>
							</button>
						</div>
					))}
					{objects.length === 0 && (
						<div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
							<p className="text-gray-500">{emptyMessage}</p>
						</div>
					)}
				</div>
			</div>

			<InputFieldModal
				isOpen={isModalOpen}
				onClose={() => setCurrentObject(undefined)}
				title={isEditing ? `Edit ${entityName}` : `Add ${entityName}`}
				inputField={inputField}
				inputValue={{ [inputField.name]: currentObject }}
				onUpdate={onObjectUpdate}
				onSave={onSave}
				saveButtonLabel={isEditing ? `Update ${entityName}` : `Save ${entityName}`}
			/>
		</>
	);
};

/**
 * These are Rebuild/System wide controls to add/edit/delete TPAs and Carriers. In the admin settings, we're able to apply these
 * TPAs and Carriers to specific customers/organizations
 */
export const TPAsCarriersPage: React.FC = () => {
	return (
		<div className="min-h-screen bg-gray-50">
			<MappingTopBox />
			<div className="ml-8 mr-8">
				<h1 className="text-3xl font-bold text-gray-800 mb-6 mt-4">TPAs & Carriers</h1>

				<div className="grid grid-cols-2 gap-8">
					<ObjectList<TPA>
						firestorePath="tpa_objects"
						inputField={TPA_INPUT_FIELD}
						title="TPAs"
						entityName="TPA"
						emptyMessage="No TPAs added yet."
						getDisplayName={(tpa) => tpa.name}
					/>
					<ObjectList<Carrier>
						firestorePath="carrier_objects"
						inputField={CARRIER_INPUT_FIELD}
						title="Carriers"
						entityName="Carrier"
						emptyMessage="No Carriers added yet."
						getDisplayName={(carrier) => carrier.name}
					/>
				</div>
			</div>
		</div>
	);
};

export default TPAsCarriersPage;
